import {
  Box,
  Flex,
  GovernmentWithFlag,
  Image,
  IntlRelativeTime,
  Tag,
  TagText,
  TCountryCode,
  Text
} from "@govlaunch/web";
import Tooltip from "@govlaunch/web/src/tooltip/Tooltip";
import React, { forwardRef, ReactElement } from "react";
import { IStoryCardFieldsFragment } from "./fragments/__generated__/StoryCardFieldsFragment.generated";
import StoryPodcastIcon from "./StoryPodcastIcon";
import { useIntl } from "react-intl";

export enum IMediaType {
  Image = "IMAGE",
  Video = "VIDEO",
  Youtube = "YOUTUBE"
}

interface IStoryCardProps {
  story: IStoryCardFieldsFragment;
  placeholderImageSrc?: string;
  translationLinkWrap?: (slug: string) => ReactElement | null;
}

const StoryCard = forwardRef<any, IStoryCardProps>(
  ({ story, placeholderImageSrc, translationLinkWrap, ...props }, ref) => {
    const image = (story.medias || []).find((media) => media.type === IMediaType.Image);
    const intl = useIntl();

    return (
      <Flex
        _hover={{
          shadow: "lg"
        }}
        transition="0.2s all"
        as="a"
        bg="white"
        border="solid 1px"
        borderColor="gray.100"
        borderRadius="md"
        paddingX={[6, 6, 8]}
        paddingY={6}
        direction="column"
        cursor="pointer"
        ref={ref}
        position="relative"
        {...props}
      >
        {story.hasPodcast && (
          <Tooltip label={intl.formatMessage({ defaultMessage: "Podcast", id: "uNMm97" })} bg="blue.600" color="white">
            <Box borderRadius="md" position="absolute" top={2} right={2}>
              <StoryPodcastIcon
                color="gray.400"
                _hover={{
                  color: "blue.600"
                }}
              />
            </Box>
          </Tooltip>
        )}
        <Flex direction="row" alignItems="center" justifyContent="space-between" mt={0} mb={4}>
          <Text as="time" fontSize="xs" color="gray.500" textTransform="uppercase">
            <IntlRelativeTime
              value={story.lastPublishedAt || story.modifiedAt || story.createdAt || new Date()}
              hideOlderThan={{ unit: "month", value: 4 }}
            />
          </Text>
          {translationLinkWrap && <Box mb="1px">{translationLinkWrap(story.slug)}</Box>}
        </Flex>
        {image && (
          <Image
            alt="story cover image"
            src={`https://imaginary-ssl.govlaunch.com/resize?quality=100&compression=0&width=326&height=163&url=${image.url}`}
            width="100%"
            maxHeight="163px"
            minHeight="163px"
            objectFit="cover"
            borderRadius="md"
            mb={4}
          />
        )}
        {!image && placeholderImageSrc && (
          <Image alt="story placeholder cover image" src={placeholderImageSrc} width="100%" borderRadius="md" mb={4} />
        )}
        <Text fontSize="xl" color="blue.600" fontWeight="bold" mt={0} mb={4} clamp={2} title={story.title}>
          {story.title}
        </Text>
        <Text fontSize="sm" mt={0} mb={4} clamp={2}>
          {story.introduction}
        </Text>
        {story.tags && story.tags.length > 0 && (
          <div>
            <Tag maxWidth="calc(100% - 24px)" title={story.tags[0].name}>
              <TagText>{story.tags[0].name}</TagText>
            </Tag>

            {story.tags.length > 1 && (
              <Box as="span" fontSize="sm" marginLeft={2} fontWeight="semibold" color="blue.800">
                +{story.tags.length - 1}
              </Box>
            )}
          </div>
        )}
        {story.government && (
          <Box mt={4}>
            <GovernmentWithFlag
              name={story.government.name}
              logo={story.government.logo}
              countryCode={story.government.city.country as TCountryCode}
            />
          </Box>
        )}
      </Flex>
    );
  }
);

StoryCard.displayName = "StoryCard";

export default StoryCard;
